/**
 * Lightweight renderer for assistant messages that follow the support-chat
 * response format (set in support_chat/llm/system_prompt.py):
 *
 *   **Diagnosis:** <text>
 *
 *   **Next steps:**
 *   - <bullet>
 *   - <bullet>
 *
 *   Tell the attendee: > <copy-paste-ready phrase>
 *
 * "Tell the attendee:" lines are elevated to a distinct quoted callout with
 * a copy button so staff can lift the phrase verbatim. Bold and bullet
 * patterns get inline styling. Everything else falls back to plain text.
 */

import { Copy, MessageSquareQuote } from 'lucide-react'
import { useState, type ReactNode } from 'react'

interface AssistantContentProps {
  content: string
}

export function AssistantContent({ content }: AssistantContentProps) {
  const blocks = parseBlocks(content)
  return (
    <div className="space-y-2">
      {blocks.map((block, i) => renderBlock(block, i))}
    </div>
  )
}


type Block =
  | { kind: 'text'; text: string }
  | { kind: 'header'; text: string }
  | { kind: 'bullets'; items: string[] }
  | { kind: 'callout'; text: string }


/**
 * Insert a paragraph break before a `**Header:**` pattern when the LLM
 * concatenates it directly onto preceding narration. The model sometimes
 * emits the conversational preamble for a tool call ("Looking up that
 * vendor for you.") and the post-tool diagnosis header on the same line,
 * which renders as:
 *   "Looking up that vendor for you.**Diagnosis:** ..."
 *
 * The line-level parseBlocks header regex below is anchored to the start
 * of a line, so it misses these. Pre-splitting on the inline pattern lets
 * the existing line-based logic pick the header up as a real header
 * block. Pattern requires the bold tag to start with uppercase AND have
 * the colon INSIDE the closing `**` (i.e. `**Diagnosis:**`, not
 * `**Diagnosis**:` — the LLM always emits the former), so we don't
 * false-match inline bold emphasis like "...**important** word..." with
 * no colon at all.
 */
const INLINE_HEADER_RE = /(\S)([ \t]*)(\*\*[A-Z][^*\n]*:\*\*)/g

function preprocessInlineHeaders(content: string): string {
  return content.replace(INLINE_HEADER_RE, '$1\n\n$3')
}


function parseBlocks(content: string): Block[] {
  const blocks: Block[] = []
  const lines = preprocessInlineHeaders(content).split('\n')
  let bullets: string[] = []
  let textBuffer: string[] = []

  const flushText = () => {
    const joined = textBuffer.join('\n').trim()
    if (joined) blocks.push({ kind: 'text', text: joined })
    textBuffer = []
  }
  const flushBullets = () => {
    if (bullets.length) {
      blocks.push({ kind: 'bullets', items: [...bullets] })
      bullets = []
    }
  }

  for (const rawLine of lines) {
    const line = rawLine.trim()
    if (!line) {
      flushText()
      flushBullets()
      continue
    }

    // "Tell the attendee:" callouts — accept optional ">" prefix on the phrase.
    const callout = /^tell the attendee[:：]\s*>?\s*(.+)$/i.exec(line)
    if (callout) {
      flushText()
      flushBullets()
      blocks.push({ kind: 'callout', text: callout[1].trim().replace(/^"|"$/g, '') })
      continue
    }

    // Bulleted line.
    const bullet = /^[-•]\s+(.+)$/.exec(line)
    if (bullet) {
      flushText()
      bullets.push(bullet[1])
      continue
    }

    // Section headers like **Diagnosis:** or **Next steps:**
    const header = /^\*\*([^*]+)\*\*\s*:?\s*(.*)$/.exec(line)
    if (header) {
      flushText()
      flushBullets()
      blocks.push({ kind: 'header', text: header[1].trim() })
      if (header[2].trim()) {
        textBuffer.push(header[2].trim())
      }
      continue
    }

    flushBullets()
    textBuffer.push(rawLine)
  }
  flushText()
  flushBullets()
  return blocks
}


function renderBlock(block: Block, key: number): ReactNode {
  switch (block.kind) {
    case 'header':
      return (
        <p
          key={key}
          className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400"
        >
          {block.text}
        </p>
      )
    case 'bullets':
      return (
        <ul key={key} className="ml-4 list-disc space-y-1 text-sm text-neutral-900 dark:text-neutral-100">
          {block.items.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ul>
      )
    case 'callout':
      return <Callout key={key} text={block.text} />
    case 'text':
    default:
      return (
        <p key={key} className="text-sm text-neutral-900 dark:text-neutral-100">
          {renderInline(block.text)}
        </p>
      )
  }
}


/** Render inline **bold** segments. */
function renderInline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(part)
    if (m) return <strong key={i}>{m[1]}</strong>
    return <span key={i}>{part}</span>
  })
}


function Callout({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Best-effort; if clipboard is unavailable, the staff can still read it.
    }
  }

  return (
    <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-900 dark:bg-blue-950/30">
      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
        <MessageSquareQuote className="h-3 w-3" />
        Tell the attendee
      </div>
      <p className="text-sm text-blue-900 dark:text-blue-100">&ldquo;{text}&rdquo;</p>
      <button
        type="button"
        onClick={copy}
        className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100"
      >
        <Copy className="h-3 w-3" />
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}
